import {
    RefCallback,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';

/**
 * Row virtualization for the DICOM tag table.
 *
 * A structure set expands to tens of thousands of tags, and a short search
 * query routinely matches nearly all of them. Rendering a table row per match
 * is what makes the dialog unresponsive, so only the rows inside the scroll
 * viewport — plus a small overscan — are mounted. The rows above and below are
 * stood in for by two spacer rows, which keeps the scrollbar the length it
 * would have if the whole list were there.
 *
 * Rows are a uniform, declared height rather than a measured one, so mapping a
 * scroll offset to a row index is exact arithmetic and the window is
 * reproducible. Enforcing that height is the dialog's side of the contract;
 * `DicomTagBrowser.browser.test.tsx` checks it against a real layout engine.
 *
 * Trade-off: rows outside the window are absent from the DOM, so the browser's
 * own find-in-page and a screen reader only see the visible window. That is
 * the price of the table being usable at all at this size.
 */

/**
 * Height of a rendered tag row, in CSS pixels. The dialog sets it on the row
 * and strips the cells' vertical padding, so nothing a row contains reaches
 * that height on its own and the row is exactly as tall as it is told to be.
 * Chosen for legibility, not derived from the theme — a table-density change
 * elsewhere must not silently move it.
 */
export const TAG_ROW_HEIGHT_PX = 27;

/**
 * Rows rendered beyond each edge of the viewport. Covers the gap between a
 * scroll event and the re-render it schedules, so fast scrolling does not
 * expose blank space.
 */
export const OVERSCAN_ROWS = 8;

/**
 * Viewport height assumed where the container cannot be measured — jsdom
 * reports every element as zero-height. Erring small only ever under-renders,
 * which the overscan absorbs; erring large would mount rows for a viewport
 * that does not exist.
 */
export const UNMEASURED_VIEWPORT_HEIGHT_PX = 600;

/** The slice of rows to mount, and the spacers standing in for the rest. */
export interface VirtualRowWindow {
    /** Index of the first row to render, inclusive. */
    startIndex: number;
    /** Index one past the last row to render. */
    endIndex: number;
    /** Height of the spacer replacing the rows before `startIndex`. */
    paddingTopPx: number;
    /** Height of the spacer replacing the rows after `endIndex`. */
    paddingBottomPx: number;
}

/**
 * Which rows fall inside the viewport at `scrollTopPx`, widened by
 * {@link OVERSCAN_ROWS} on both sides.
 *
 * `paddingTopPx + rendered row heights + paddingBottomPx` always equals
 * `rowCount * TAG_ROW_HEIGHT_PX`, so the scroll height does not change as the
 * window moves. An empty list falls out of the arithmetic as an empty window.
 */
export function computeVirtualRowWindow(
    rowCount: number,
    scrollTopPx: number,
    viewportHeightPx: number
): VirtualRowWindow {
    const maxScrollTop = Math.max(
        0,
        rowCount * TAG_ROW_HEIGHT_PX - viewportHeightPx
    );
    const scrollTop = Math.min(Math.max(scrollTopPx, 0), maxScrollTop);

    // A viewport that starts mid-row shows one more row than it is tall.
    const firstVisible = Math.floor(scrollTop / TAG_ROW_HEIGHT_PX);
    const visibleCount = Math.ceil(viewportHeightPx / TAG_ROW_HEIGHT_PX) + 1;

    const startIndex = Math.max(0, firstVisible - OVERSCAN_ROWS);
    const endIndex = Math.min(
        rowCount,
        firstVisible + visibleCount + OVERSCAN_ROWS
    );

    return {
        startIndex,
        endIndex,
        paddingTopPx: startIndex * TAG_ROW_HEIGHT_PX,
        paddingBottomPx: (rowCount - endIndex) * TAG_ROW_HEIGHT_PX,
    };
}

/** The row index a scroll offset sits in; the window only moves with it. */
function rowAtOffset(scrollTopPx: number): number {
    return Math.floor(Math.max(scrollTopPx, 0) / TAG_ROW_HEIGHT_PX);
}

/** Scroll container state plus the row window it implies. */
export interface VirtualRows extends VirtualRowWindow {
    /** Attach to the scrolling element that wraps the table. */
    scrollRef: RefCallback<HTMLElement>;
    /** Return the container to the top; call when the row set is replaced. */
    scrollToTop: () => void;
}

/**
 * Tracks the scroll offset and height of the container returned through
 * `scrollRef`, and derives the row window from them.
 *
 * The container is held in state rather than a ref because it is mounted and
 * unmounted with the dialog, and the observers have to follow it.
 */
export function useVirtualRows(rowCount: number): VirtualRows {
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(
        null
    );
    const [scrollTopPx, setScrollTopPx] = useState(0);
    const [viewportHeightPx, setViewportHeightPx] = useState(
        UNMEASURED_VIEWPORT_HEIGHT_PX
    );

    // Measured before paint, so the dialog opens on the real viewport rather
    // than rendering the assumed one first and correcting it a frame later.
    useLayoutEffect(() => {
        if (!scrollElement) {
            return;
        }

        const measure = () =>
            setViewportHeightPx(
                scrollElement.clientHeight || UNMEASURED_VIEWPORT_HEIGHT_PX
            );

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(scrollElement);
        return () => observer.disconnect();
    }, [scrollElement]);

    useEffect(() => {
        if (!scrollElement) {
            return;
        }

        // Scroll events outrun rendering, so they are collapsed onto animation
        // frames. Offsets within one row imply the same window — common on a
        // trackpad, where a frame can move a few pixels — and are dropped
        // rather than re-rendering the table for an identical result.
        let frame = 0;
        const handleScroll = () => {
            if (frame !== 0) {
                return;
            }
            frame = requestAnimationFrame(() => {
                frame = 0;
                setScrollTopPx((previous) =>
                    rowAtOffset(previous) ===
                    rowAtOffset(scrollElement.scrollTop)
                        ? previous
                        : scrollElement.scrollTop
                );
            });
        };

        scrollElement.addEventListener('scroll', handleScroll, {
            passive: true,
        });
        return () => {
            if (frame !== 0) {
                cancelAnimationFrame(frame);
            }
            scrollElement.removeEventListener('scroll', handleScroll);
        };
    }, [scrollElement]);

    const scrollToTop = useCallback(() => {
        if (scrollElement) {
            scrollElement.scrollTop = 0;
        }
        setScrollTopPx(0);
    }, [scrollElement]);

    return {
        ...computeVirtualRowWindow(rowCount, scrollTopPx, viewportHeightPx),
        scrollRef: setScrollElement,
        scrollToTop,
    };
}
